import { ApiError, get, post } from '@/api/client';
import { cameThroughTrade, forgetDestination } from '@/auth/destination';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { commitGeneration } from '@/shell/context';
import { Button } from '@/ui';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Field, KontoBox, errorText, failureText, hint, seedFromQuery } from './shared';

/** GET /account/me */
export interface Me {
  id: number;
  email: string;
  name: string;
  lastname: string;
  verified: boolean;
  /** false for a demo visitor, who came in by the mailed link; they choose one here (owner, 2026-09-28) */
  has_password?: boolean;
  memberships: { tenant_id: number; role: string; name: string }[];
}
/** POST /account/companies: 201 on creation, 200 with repeat on the same request_key again. */
interface Created {
  repeat?: true;
  access?: 'lost';
  tenant?: { id: number; name?: string };
  role?: string;
  token?: string | null;
}

/** What the resend button says after POST /account/verification answered. */
export function verificationText(status: number, code: string | null, message?: string): string {
  if (status === 202) return T('Vi har sendt en ny e-post med en lenke for å bekrefte adressen.');
  if (status === 200) return T('Adressen er allerede bekreftet. Last siden på nytt.');
  if (code === 'no_usable_address')
    return T('Adressen på kontoen kan ikke motta e-post. Ta kontakt med oss.');
  if (code === 'mail_unavailable') return T('Vi kan ikke sende e-post akkurat nå. Prøv igjen senere.');
  if (code) return errorText(code, message);
  return T('Fikk ikke svar fra tjeneren. Prøv igjen.');
}

/** The first company for a person who has none, or a separate one beside those they belong to. */
export default function OpprettBedrift() {
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [me, setMe] = useState<Me | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadErr(null);
    get<Me>('/account/me')
      .then(setMe)
      .catch((ex) => setLoadErr(failureText(ex)));
  }, []);
  useEffect(load, []);

  // resend the verification mail
  const [resend, setResend] = useState<{ busy: boolean; text: string | null }>({ busy: false, text: null });
  async function resendVerification() {
    setResend({ busy: true, text: null });
    try {
      const r = await post<{ verified: boolean }>('/account/verification', {});
      setResend({ busy: false, text: verificationText(r.verified ? 200 : 202, null) });
    } catch (ex) {
      const code = ex instanceof ApiError ? ex.code : null;
      setResend({
        busy: false,
        text: verificationText(
          ex instanceof ApiError ? ex.status : 0,
          code,
          ex instanceof ApiError ? ex.message : undefined,
        ),
      });
    }
  }

  // the company form: one request_key per mount, kept across retries so the server can answer the same request once
  const [name, setName] = useState('');
  const [orgNumber, setOrgNumber] = useState(''); // optional; checked by the server and the register (plan 0028 slice 3)
  // what the Enhetsregisteret says for the number, shown under it as it is typed (#191: nothing showed
  // before saving); an empty name field takes the register's name
  const [register, setRegister] = useState<{
    org: string;
    valid: boolean;
    found: boolean | null;
    name: string | null;
  } | null>(null);
  const digits = orgNumber.replace(/\s/g, '');
  useEffect(() => {
    if (!/^\d{9}$/.test(digits)) return setRegister(null);
    let gone = false;
    get<{ valid: boolean; found: boolean | null; name: string | null }>(`/account/brreg/${digits}`)
      .then((r) => {
        if (gone) return;
        setRegister({ org: digits, ...r });
        if (r.name) setName((n) => n.trim() || r.name || '');
      })
      .catch(() => !gone && setRegister(null));
    return () => {
      gone = true;
    };
  }, [digits]);
  // a company made from the trade route (`demo=bygg` here, or kept from the sign-up across the
  // confirmation mail) starts with planning off, its week «Uka»; everything else with it on (#118)
  const { search } = useLocation();
  const [scheduling, setScheduling] = useState(() => !(seedFromQuery(search).demo || cameThroughTrade()));
  const [password, setPassword] = useState('');
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lost, setLost] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!me) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await post<Created>('/account/companies', {
        name,
        scheduling_enabled: scheduling,
        ...(orgNumber.trim() ? { org_number: orgNumber.replace(/\s/g, '') } : {}),
        request_key: requestKey,
        ...(me.has_password === false ? { password } : {}),
      });
      if (r.token) {
        setSession(r.token, {
          id: me.id,
          name: me.name,
          lastname: me.lastname,
          email: me.email,
          role: 'admin',
        });
        commitGeneration(me.id, null, null); // the token now names the new company; nothing from before is readable
        forgetDestination(); // arrived: a later login starts from the company (plan 0028 slice 1)
        nav('/', { replace: true });
        return;
      }
      setLost(true); // repeat with access lost, or a creation the server could not issue a token for
    } catch (ex) {
      if (ex instanceof ApiError) {
        if (ex.code === 'request_key_reused') {
          setRequestKey(crypto.randomUUID());
          setErr(T('Forespørselen kunne ikke gjenbrukes. Trykk på knappen en gang til.'));
        } else if (ex.code === 'verify_email_first') {
          setMe({ ...me, verified: false });
        } else setErr(failureText(ex));
      } else {
        setErr(
          T(
            'Vi vet ikke om bedriften ble opprettet. Trykk på knappen igjen, så fortsetter samme forespørsel.',
          ),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <KontoBox title={T('Opprett bedrift')}>
      {!me ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          {loadErr ? (
            <>
              <div className="al dgr" style={{ margin: 0 }}>
                {loadErr}
              </div>
              <Button onClick={load}>{T('Prøv igjen')}</Button>
            </>
          ) : (
            <span style={hint}>{T('Laster …')}</span>
          )}
        </div>
      ) : lost ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          <div className="al warn" style={{ margin: 0 }}>
            {T('Bedriften ble opprettet, men du har ikke lenger tilgang til den.')}
          </div>
          <Link to="/login" style={hint}>
            {T('Til innlogging')}
          </Link>
        </div>
      ) : !me.verified ? (
        <div className="opt" style={{ display: 'grid', gap: 10 }}>
          <div className="al info" style={{ margin: 0 }}>
            {T(
              'E-postadressen må bekreftes før du kan opprette en bedrift. Sjekk innboksen for lenken vi sendte.',
            )}
          </div>
          <span style={hint}>{me.email}</span>
          {resend.text && (
            <output className="al info" style={{ margin: 0 }}>
              {resend.text}
            </output>
          )}
          <Button onClick={resendVerification} disabled={resend.busy}>
            {resend.busy ? '…' : T('Send bekreftelsen på nytt')}
          </Button>
        </div>
      ) : (
        <form className="opt" onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          {me.memberships.length > 0 && (
            <span style={hint}>
              {T('Dette oppretter en ny, separat bedrift der du alene er administrator.')}{' '}
              <Link to="/">{T('Tilbake til bedriften')}</Link>
            </span>
          )}
          <Field
            label={T('Navn på bedriften')}
            type="text"
            autoComplete="organization"
            minLength={2}
            maxLength={255}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Field
            label={T('Organisasjonsnummer (valgfritt)')}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={24}
            value={orgNumber}
            onChange={(e) => setOrgNumber(e.target.value)}
          />
          {register?.org === digits && (
            <output style={{ ...hint, display: 'block' }} data-testid="brreg">
              {!register.valid
                ? T('Nummeret har feil kontrollsiffer. Sjekk at det er skrevet riktig.')
                : register.found
                  ? T('Enhetsregisteret: {name}', { name: register.name ?? '' })
                  : register.found === false
                    ? T('Fant ikke nummeret i Enhetsregisteret.')
                    : T('Enhetsregisteret svarte ikke. Nummeret lagres likevel.')}
            </output>
          )}
          {me.has_password === false && (
            <>
              <Field
                label={T('Velg passord (minst 8 tegn)')}
                type="password"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <span style={hint}>
                {T('Du logger inn med {email} og dette passordet, på nettsiden og i appen.', {
                  email: me.email,
                })}
              </span>
            </>
          )}
          <div className="fld" style={{ gridTemplateColumns: '1fr' }}>
            <span style={{ font: '600 12px var(--ui)', color: 'var(--ink3)' }}>{T('Innstillinger')}</span>
            <span style={hint}>{T('Tidssone: Europe/Oslo · Språk: norsk (bokmål)')}</span>
          </div>
          <label className="fld" style={{ gridTemplateColumns: 'auto 1fr', border: 0 }}>
            <input type="checkbox" checked disabled />
            <span style={hint}>{T('Timer (alltid på)')}</span>
          </label>
          <label className="fld" style={{ gridTemplateColumns: 'auto 1fr', border: 0 }}>
            <input type="checkbox" checked={scheduling} onChange={(e) => setScheduling(e.target.checked)} />
            <span style={hint}>{T('Vaktplan')}</span>
          </label>
          {err && <div className="al dgr">{err}</div>}
          <Button tone="pri" type="submit" disabled={busy}>
            {busy ? '…' : T('Opprett bedrift')}
          </Button>
        </form>
      )}
    </KontoBox>
  );
}
